"use client";

import React from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
const steps = [{
  number: "01",
  title: "Первичная консультация",
  description: "Детальный разбор вашей ситуации и первичная оценка правовых рисков. На этом этапе мы определяем цели, анализируем имеющиеся документы и формируем общее видение перспектив дела совместно с адвокатом.",
  images: ["https://wpvc-images.s3.us-east-1.amazonaws.com/images/1602611/img/legal_consultation_office.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1602611/img/lawyer_client_meeting.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1602611/img/legal_documents_desk.png"]
}, {
  number: "02",
  title: "Правовой анализ и стратегия",
  description: "Глубокое изучение нормативной базы и судебной практики. Мы разрабатываем индивидуальную стратегию защиты, собираем доказательную базу и готовим правовое обоснование для достижения наилучшего результата.",
  images: ["https://wpvc-images.s3.us-east-1.amazonaws.com/images/1602611/img/case_analysis_research.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1602611/img/legal_strategy_planning.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1602611/img/law_books_library.png"]
}, {
  number: "03",
  title: "Досудебное урегулирование",
  description: "Подготовка претензий и проведение переговоров с оппонентами. Мы стремимся разрешить спор с минимальными издержками для клиента, используя медиативные техники и аргументированную юридическую позицию.",
  images: ["https://wpvc-images.s3.us-east-1.amazonaws.com/images/1602611/img/negotiation_table.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1602611/img/pre_trial_settlement.png"]
}, {
  number: "04",
  title: "Судебное представительство",
  description: "Профессиональная защита интересов в судах общей юрисдикции и арбитраже. Полное сопровождение процесса: от подачи иска до контроля исполнения решения суда и представления интересов в апелляции.",
  images: ["https://wpvc-images.s3.us-east-1.amazonaws.com/images/1602611/img/courtroom_representation.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1602611/img/arbitration_hearing.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1602611/img/successful_verdict.png"]
}];
function TimelineStep({
  step,
  index
}: {
  step: (typeof steps)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <div data-section-id="114694" ref={ref} data-index={index} className={`relative pl-12 md:pl-40 pb-24 last:pb-0 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16"}`}>
      {}
      <div className="absolute left-4 md:left-24 top-3 flex justify-center w-0 z-10">
        <div className={`w-2.5 h-2.5 bg-primary rounded-sm transition-transform duration-700 delay-300 ${isVisible ? "scale-100" : "scale-0"}`} />
      </div>

      {}
      <div className="md:absolute md:left-0 md:top-0 md:w-16 md:text-right text-5xl md:text-6xl font-mono text-secondary/20 mb-4 md:mb-0 select-none tracking-tighter">
        {step.number}
      </div>

      {}
      <div className="max-w-4xl">
        <h3 className="text-2xl md:text-3xl font-serif mb-4 text-foreground tracking-tight">
          {step.title}
        </h3>
        <p className="text-muted-foreground mb-8 text-base md:text-lg leading-relaxed max-w-2xl font-sans">
          {step.description}
        </p>

        {}
        <Carousel opts={{
        align: "start"
      }} className="w-full">
          <CarouselContent>
            {step.images.map((img, imgIndex) => <CarouselItem key={imgIndex} data-index={imgIndex} className="md:basis-1/2 lg:basis-1/2">
                <div className="aspect-[4/3] relative border border-border overflow-hidden group bg-muted rounded-sm">
                  <img src={img} alt={`${step.title} - этап работы ${imgIndex + 1}`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                </div>
              </CarouselItem>)}
          </CarouselContent>
          <div className="hidden md:block">
            <CarouselPrevious className="left-4 rounded-sm bg-background/90 backdrop-blur-sm border-border hover:bg-primary hover:text-primary-foreground text-foreground transition-colors" />
            <CarouselNext className="right-4 rounded-sm bg-background/90 backdrop-blur-sm border-border hover:bg-primary hover:text-primary-foreground text-foreground transition-colors" />
          </div>
        </Carousel>
      </div>
    </div>;
}
export default function ProjectPath() {
  const {
    ref: containerRef,
    isVisible: isContainerVisible
  } = useRevealOnScroll({
    threshold: 0.05
  });
  return <section id="с-чего-начать" className="bg-background text-foreground py-24 lg:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {}
        <div className="mb-20 md:mb-32 max-w-3xl">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif tracking-tight mb-6 text-primary">
            С чего начать?
          </h2>
          <p className="text-secondary text-lg md:text-xl leading-relaxed font-sans">
            Прозрачный и последовательный процесс юридической защиты. От первой встречи до 
            представления ваших интересов в суде и достижения поставленных целей.
          </p>
        </div>

        {}
        <div ref={containerRef} className="relative">
          {}
          <div className="absolute left-4 md:left-24 top-0 bottom-0 flex flex-col items-center w-0">
            <div className="w-px bg-border transition-all duration-[2500ms] ease-in-out origin-top" style={{
            height: isContainerVisible ? "100%" : "0%"
          }} />
          </div>

          {}
          <div className="relative pt-2">
            {steps.map((step, index) => <TimelineStep key={index} step={step} index={index} />)}
          </div>
        </div>
      </div>
    </section>;
}